import { useCallback, useEffect, useRef, useState } from 'react';

import { useMetronomeStore } from '../pages/View/components/plugins/MetronomePlugin/MetronomePlugin';
import { prepareClickSound } from '../utils/clickSounds';
import { resolveSoundType, scheduleClickSound } from '../utils/metronomeClick';
import { getBeatsPerBar } from '../utils/songTiming';

// Silence before the first click. Output that has just woken — Bluetooth headphones most of all —
// fades in or drops its first sound.
const LEAD_IN = 0.6;

/**
 * Tapping a rhythm along to a click: a bar of count-in at `bpm`, then up to `bars` bars to tap
 * through, until the last of them or stop(). With `countInBar` the count-in is the first of those
 * bars — a chart that comes in during it — so what is tapped over it is kept. Taps come back in beats
 * from the first recorded downbeat as the audio clock had it, which is late by however long the sound takes to be heard —
 * see alignTaps for why that is left alone.
 *
 * Its own context, with every click scheduled up front: the window is known before it starts, so
 * there is nothing for the metronome engine's worker to keep feeding, and it leaves the song page's
 * metronome alone.
 */
const useTapRhythm = ({ bpm, timeSignature }) => {
  const [phase, setPhase] = useState('idle');
  // Beats since the first click of the count-in; -1 before it
  const [beat, setBeat] = useState(-1);
  const [taps, setTaps] = useState([]);
  // Whether the session's count-in is its first bar, for the count on screen to say which bar is which
  const [countInBar, setCountInBar] = useState(false);
  const sessionRef = useRef(null);
  const beatsPerBar = getBeatsPerBar(timeSignature);
  const tempo = Number(bpm);
  const canTap = tempo > 0 && Boolean(window.AudioContext || window.webkitAudioContext);

  const endSession = useCallback(() => {
    const session = sessionRef.current;

    if (!session) return;

    session.timers.forEach(clearTimeout);
    session.ctx.close();
    sessionRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    endSession();
    setPhase('idle');
    setTaps((list) => (list.length ? [] : list));
  }, [endSession]);

  // Ends the recording where it is, keeping what was tapped
  const stop = useCallback(() => {
    endSession();
    setPhase('done');
  }, [endSession]);

  useEffect(() => endSession, [endSession]);

  // Call from a tap: iOS only lets a context start inside one
  const start = useCallback(
    (bars, { countInBar: overCount = false } = {}) => {
      endSession();

      if (!canTap) return;

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const { soundType, volume } = useMetronomeStore.getState();
      const secondsPerBeat = 60 / tempo;
      const clicks = beatsPerBar * (overCount ? bars : 1 + bars);
      // No downbeat until the clicks are scheduled, so a tap before then counts for nothing
      const session = { ctx, timers: [], downbeat: Infinity, secondsPerBeat };

      sessionRef.current = session;
      setTaps([]);
      setBeat(-1);
      setCountInBar(overCount);
      setPhase('counting');

      // Scheduled once the context runs and the click is rendered, so neither eats into the count-in
      Promise.all([ctx.resume(), prepareClickSound(resolveSoundType(soundType))]).then(() => {
        if (sessionRef.current !== session) return;

        const first = ctx.currentTime + LEAD_IN;
        // The count on screen waits for the sound to reach the speakers, as far as the browser knows
        const shown = (time) => Math.max(0, (time - ctx.currentTime + (ctx.outputLatency || 0)) * 1000);

        session.downbeat = overCount ? first : first + beatsPerBar * secondsPerBeat;

        for (let i = 0; i < clicks; i++) {
          const time = first + i * secondsPerBeat;

          scheduleClickSound(ctx, time, i % beatsPerBar === 0 ? 'downbeat' : 'regular', { soundType, volume });
          session.timers.push(
            setTimeout(() => {
              setBeat(i);
              // Over the count-in, the count is already the first bar being recorded
              if (i === (overCount ? 0 : beatsPerBar)) setPhase('recording');
            }, shown(time)),
          );
        }

        session.timers.push(setTimeout(stop, shown(first + clicks * secondsPerBeat)));
      });
    },
    [beatsPerBar, canTap, endSession, stop, tempo],
  );

  const tap = useCallback((timeStamp = performance.now()) => {
    const session = sessionRef.current;

    if (!session) return;

    // Where the audio clock was when the key went down, not when the event got here
    const at = session.ctx.currentTime - Math.max(0, performance.now() - timeStamp) / 1000;

    setTaps((list) => [...list, (at - session.downbeat) / session.secondsPerBeat]);
  }, []);

  return {
    phase,
    isRunning: phase === 'counting' || phase === 'recording',
    beat,
    beatsPerBar,
    countInBar,
    taps,
    canTap,
    start,
    tap,
    stop,
    cancel,
  };
};

export default useTapRhythm;
